import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    makeStyles, Button, Dialog, DialogContent, AppBar, Toolbar,
    IconButton, Typography, Slide, Chip,
} from '@material-ui/core';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import CloseIcon from '@material-ui/icons/Close';
import {
    CachedIcon,
    CheckBoxIcon,
    ThumbUpAltOutlinedIcon,
    CheckBoxOutlineBlankIcon,
    PrintOutlinedIcon,
} from 'components/icons';

// todo: check why the datepicker doesn't work
import 'react-datepicker/dist/react-datepicker.css';
import { useQuery, useMutation } from '@apollo/client';
import ModalUtils from 'utils/ModalUtils';
import DateUtils from 'lib/DateUtils';
import NumberUtils from 'lib/NumberUtils';
import { useHistory } from 'react-router';
import { modules } from 'utils/enum/modules';
import { GeneralAction } from 'utils/enum/AccountingEnum';
import Header from 'components/widgets/Header';
import LabelValue from 'components/widgets/LabelValue';
import Table from 'components/widgets/Table';
import clsx from 'clsx';

import { DataSort, FetchPolicy } from 'utils/enum/Core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Badge from '@material-ui/core/Badge';
import InputSearch from 'components/widgets/InputSearch';
import CircularProgress from '@material-ui/core/CircularProgress';
import AccountingStyles from 'styles/modules/accounting/AccountingStyles';
import DialogActionMessage from 'components/widgets/DialogActionMessage';
import printJS from 'print-js';
import { PartsPurchaseSubModules, PurchaseOrderStatus } from 'utils/enum/PartsPurchaseEnum';
import PartsQuery from 'services/graphQL/query/parts/PartsQuery';
import PartsMutation from 'services/graphQL/mutate/parts/PartsMutation';

const useStyle = makeStyles((theme) => AccountingStyles.checkActionList(theme));

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const printLimit = 30;

export const ACTION_TYPES = {
    SET_ON_POPUP_CLOSE: 'setOnPopupClose',
    SET_INITIAL_STATE: 'setInitialState',
    SET_STATE_VALUES: 'setStateValue',
    LOAD_MORE: 'setLoadMore',
    SET_PARAMS: 'setParams',
    SET_TABLE: 'setTable',
    SET_TABLE_APPROVED: 'setTableApproved',
    SET_APPROVE_ALL: 'setApproveAll',
};

const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPES.SET_ON_POPUP_CLOSE: {
        return {
            ...state,
            couldLostData: true,
        };
    }
    case ACTION_TYPES.SET_TABLE: {
        return {
            ...state,
            table: action.value,
        };
    }
    case ACTION_TYPES.SET_PARAMS: {
        return {
            ...state,
            table: {
                records: [],
                totalCount: 0,
            },
            params: action.value,
        };
    }
    case ACTION_TYPES.LOAD_MORE: {
        return {
            ...state,
            params: action.value,
        };
    }
    case ACTION_TYPES.SET_INITIAL_STATE: {
        return action.value;
    }
    case ACTION_TYPES.SET_STATE_VALUES: {
        return { ...state, ...action.value };
    }
    case ACTION_TYPES.SET_APPROVE_ALL: {
        const isChecked = action.value;
        const totalRecords = state.table.records.length;
        const newRecords = isChecked && totalRecords > printLimit
            ? state.table.records.map((item, index) => ({
                ...item,
                isChecked: index < printLimit,
            }))
            : state.table.records.map((item) => ({
                ...item,
                isChecked,
            }));

        const exceedLimit = isChecked && totalRecords > printLimit;

        return {
            ...state,
            table: {
                ...state.table,
                records: newRecords,
            },
            isDirty: newRecords.filter((c) => c.isChecked).length > 0,
            exceedPrintLimit: exceedLimit,
        };
    }
    case ACTION_TYPES.SET_TABLE_APPROVED: {
        const newRecords = state.table.records.map((item) => ({
            ...item,
            isChecked: item.purchaseOrderId === action.value.purchaseOrderId ? !item.isChecked : item.isChecked,
        }));

        const selectedRecords = newRecords.filter((c) => c.isChecked);
        let exceedLimit = false;
        if (selectedRecords.length > printLimit) {
            exceedLimit = true;
        }

        return {
            ...state,
            table: {
                ...state.table,
                records: exceedLimit ? state.table.records : newRecords,
            },
            isDirty: newRecords.filter((c) => c.isChecked).length > 0,
            exceedPrintLimit: exceedLimit,
        };
    }
    default:
        return state;
    }
};

const PurchaseActionList = (props) => {
    const { actionType } = props;
    const initialState = {
        params: {
            paginate: {
                init: 0,
                limit: 50,
                ignoreLimit: true,
            },
            search: '',
            status: actionType === GeneralAction.APPROVE ? PurchaseOrderStatus.DRAFT : PurchaseOrderStatus.APPROVED,
        },
        table: {
            records: [],
            totalCount: 0,
        },
        isDirty: false,
        couldLostData: false,
        exceedPrintLimit: false,
        confirmApproveOpen: false,
        confirmPrintOpen: false,
        printing: false,
    };

    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const classes = useStyle();

    const [state, dispatch] = useReducer(reducer, initialState);
    const {
        isDirty, couldLostData, params,
        table, exceedPrintLimit, confirmApproveOpen, confirmPrintOpen, printing,
    } = state;

    const approvedRecords = table?.records.filter((c) => c.isChecked).map((c) => c.purchaseOrderId);

    const {
        data, loading, error, refetch,
    } = useQuery(PartsQuery.GET_PURCHASE_LIST, {
        variables: {
            paginate: params.paginate,
            filter: {
                search: params.search,
                status: actionType === GeneralAction.APPROVE ? PurchaseOrderStatus.DRAFT : PurchaseOrderStatus.APPROVED,
                lotName: params.lotName,
            },
            sort: [
                { fieldName: 'purchaseOrderId', dir: DataSort.DESC },
            ],
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const onPopupClose = (event, forceClose = false) => {
        if (isDirty && !forceClose) {
            dispatch(
                {
                    type: ACTION_TYPES.SET_ON_POPUP_CLOSE,
                },
            );
            return;
        }

        history.push(`/${modules.PARTS}/${PartsPurchaseSubModules.PURCHASE_ORDER}`);
    };

    const reload = () => {
        dispatch({
            type: ACTION_TYPES.SET_INITIAL_STATE,
            value: initialState,
        });
        refetch();
    };

    const [addRecord, { loading: isSaving }] = useMutation(PartsMutation.CHANGE_STATUS_PURCHASE_ORDERS, {
        onCompleted: (mutationData) => {
            if (mutationData?.changePurchaseStatus) {
                ModalUtils.successMessage(null, 'State change was successful');

                dispatch({
                    type: ACTION_TYPES.SET_STATE_VALUES,
                    value: {
                        isDirty: false,
                    },
                });

                reload();
            }
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage(null, errorMessage);
        },
    });

    const onSave = async () => {
        await addRecord({
            variables: {
                record: {
                    ids: approvedRecords,
                    status: actionType === GeneralAction.APPROVE ? PurchaseOrderStatus.APPROVED : PurchaseOrderStatus.VOID,
                },
            },
        });
    };

    const loadMore = () => {
        const currentOffset = table?.records?.length || 0;
        const { paginate } = params;

        dispatch({
            type: ACTION_TYPES.LOAD_MORE,
            value: {
                ...params,
                paginate: {
                    ...paginate,
                    init: currentOffset,
                },
            },
        });
    };

    const onSearch = (text) => {
        const { paginate } = params;

        dispatch({
            type: ACTION_TYPES.SET_PARAMS,
            value: {
                ...params,
                search: text,
                paginate: {
                    ...paginate,
                    init: 0,
                },
            },
        });
    };

    const [printCheck] = useMutation(PartsMutation.PRINT_PURCHASE_ORDERS, {
        onCompleted: (mutationData) => {
            if (mutationData?.printPurchaseOrders) {
                printJS({
                    printable: mutationData?.printPurchaseOrders,
                    type: 'pdf',
                    showModal: false,
                    onLoadingEnd: () => {
                        dispatch({
                            type: ACTION_TYPES.SET_STATE_VALUES,
                            value: {
                                printing: false,
                            },
                        });
                        reload();
                    },
                });
            } else {
                dispatch({
                    type: ACTION_TYPES.SET_STATE_VALUES,
                    value: {
                        printing: false,
                    },
                });
            }
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage(null, errorMessage);
            dispatch({
                type: ACTION_TYPES.SET_STATE_VALUES,
                value: {
                    printing: false,
                },
            });
        },
    });

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
            return;
        }

        if (!loading) {
            const { getPurchaseOrderList } = data;

            dispatch({
                type: ACTION_TYPES.SET_TABLE,
                value: {
                    records: getPurchaseOrderList.data,
                    totalCount: getPurchaseOrderList.totalCount,
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, loading, error]);

    const getFormattedText = (status, value) => {
        const textColorCls = status.toLowerCase() === PurchaseOrderStatus.VOID.toLowerCase() ? classes.voidCls : '';
        return <span className={textColorCls}>{value}</span>;
    };

    const columns = [
        {
            Header: (header) => {
                const { data: items } = header;
                const allApproved = items.length > 0 && items.filter((c) => c._original.isChecked).length === items.length;

                return allApproved ? (
                    <CheckBoxIcon
                        fontSize="small"
                        className={classes.highlightedColor}
                        onClick={() => dispatch({ type: ACTION_TYPES.SET_APPROVE_ALL, value: false })}
                    />
                )
                    : (
                        <CheckBoxOutlineBlankIcon
                            fontSize="small"
                            className={classes.highlightedColor}
                            onClick={() => dispatch({ type: ACTION_TYPES.SET_APPROVE_ALL, value: true })}
                        />
                    );
            },
            Cell: (cell) => {
                const {
                    original: {
                        purchaseOrderId, isChecked,
                    },
                } = cell;

                return (
                    isChecked ? (
                        <CheckBoxIcon
                            className={classes.highlightedColor}
                            onClick={() => dispatch({ type: ACTION_TYPES.SET_TABLE_APPROVED, value: { purchaseOrderId } })}
                        />
                    )
                        : (
                            <CheckBoxOutlineBlankIcon
                                className={classes.highlightedColor}
                                onClick={() => dispatch({ type: ACTION_TYPES.SET_TABLE_APPROVED, value: { purchaseOrderId } })}
                            />
                        )
                );
            },
            width: 60,
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnCenter),
            className: clsx(classes.columnStyle, classes.columnCenter),
            sortable: false,
            disableSortBy: false,
            filterable: false,
        },
        {
            minWidth: 30,
            width: 30,
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
            Cell: (cell) => cell.viewIndex + 1,
        },
        {
            Header: 'PO #',
            minWidth: 80,
            width: 70,
            id: 'PurchaseOrderId',
            accessor: 'purchaseOrderId',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
            Cell: ({ value, original: { status } }) => getFormattedText(status, value),
        },
        {
            Header: 'Vendor',
            minWidth: 200,
            width: 200,
            id: 'Vendor',
            accessor: 'vendor',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
            Cell: ({ value, original: { status } }) => getFormattedText(status, value),
        },
        {
            Header: 'Shipping Method',
            minWidth: 150,
            width: 150,
            id: 'ShippingMethod',
            accessor: 'shippingMethod',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
            Cell: ({ value, original: { status } }) => getFormattedText(status, value),
        },
        {
            Header: 'Parts Clerk',
            minWidth: 150,
            width: 150,
            id: 'partsClerk',
            accessor: 'partsClerk',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
            Cell: ({ value, original: { status } }) => getFormattedText(status, value),
        },
        {
            Header: 'Order Date',
            minWidth: 100,
            width: 100,
            id: 'OrderDate',
            accessor: 'orderDate',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnCenter),
            className: clsx(classes.columnStyle, classes.columnCenter),
            Cell: ({ value, original: { status } }) => getFormattedText(status, DateUtils.getOnlyDate(value)),
        },
        {
            Header: 'Total',
            minWidth: 100,
            width: 100,
            id: 'Total',
            accessor: 'total',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
            className: clsx(classes.columnStyle, classes.columnRight),
            Cell: ({ value, original: { status } }) => getFormattedText(status, NumberUtils.applyCurrencyFormat(value)),
        },
        {
            Header: 'Status',
            minWidth: 120,
            width: 120,
            id: 'Status',
            accessor: 'status',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnCenter),
            className: clsx(classes.columnStyle, classes.columnCenter),
            Cell: (cellData) => {
                const { original: { status } } = cellData;
                let badgeStatus = classes.primary;
                if (status.toLowerCase() === PurchaseOrderStatus.DRAFT.toLowerCase()) badgeStatus = classes.badgeCleared;
                else if (status.toLowerCase() === PurchaseOrderStatus.APPROVED.toLowerCase()) badgeStatus = classes.badgeApproved;
                else if (status.toLowerCase() === PurchaseOrderStatus.VOID.toLowerCase()) badgeStatus = classes.badgeVoid;
                else if (status.toLowerCase() === PurchaseOrderStatus.RECEIVED.toLowerCase()) badgeStatus = classes.badgePrinted;

                return <Chip size="small" label={status} className={badgeStatus} />;
            },
        },
        {
            Header: 'Notes',
            minWidth: 90,
            id: 'Notes',
            accessor: 'notes',
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
            Cell: ({ value, original: { status } }) => getFormattedText(status, value),
        },
    ];

    const checkedAll = table.records.length > 0 && table.records.filter((c) => c.isChecked).length === table.records.length;
    const selectedRecords = table.records.filter((c) => c.isChecked).map((record) => record.checkNumber);
    const recordsAsStrings = `#${selectedRecords.join(', #')}`;
    let printConfirmMessage = '';
    if (selectedRecords.length === 1) {
        printConfirmMessage = `Are you sure you want to print this record: ${recordsAsStrings}?`;
    } else {
        printConfirmMessage = `Are you sure you want to print these records: ${recordsAsStrings}?`;
    }

    const showMobileLayout = (
        <List className={classes.list}>
            {table?.records.map(({
                purchaseOrderId, vendorName, notes, total, isChecked,
            }) => {
                const secondaryText = (
                    <>
                        <span className={classes.flex}>
                            <Typography component="span" className={classes.textBold} color="textPrimary" align="left" display="inline">
                                Total : &nbsp;
                            </Typography>
                            <Typography component="span" className={classes.text} color="textSecondary" align="left" display="inline">
                                {NumberUtils.applyCurrencyFormat(total)}
                            </Typography>
                        </span>
                        {notes && (
                            <span className={classes.flex}>
                                <Typography component="span" className={classes.textBold} color="textPrimary" align="left" display="inline">
                                    Memo : &nbsp;
                                </Typography>
                                <Typography component="span" className={classes.text} color="textSecondary" align="left" display="inline">
                                    {notes}
                                </Typography>
                            </span>
                        )}
                    </>
                );

                return (
                    <React.Fragment key={purchaseOrderId}>
                        <ListItem
                            className={clsx(classes.listItem, isChecked ? classes.listItemApproved : '')}
                            button
                            onClick={() => dispatch({ type: ACTION_TYPES.SET_TABLE_APPROVED, value: { purchaseOrderId } })}
                        >
                            <ListItemText primary={vendorName} secondary={secondaryText} />
                            <ListItemAvatar className={classes.checkboxSelector}>
                                {isChecked ? <CheckBoxIcon className={classes.highlightedColor} />
                                    : <CheckBoxOutlineBlankIcon className={classes.highlightedColor} />}
                            </ListItemAvatar>
                        </ListItem>
                    </React.Fragment>
                );
            })}
        </List>
    );

    const exceedPrintClose = () => {
        dispatch({
            type: ACTION_TYPES.SET_STATE_VALUES,
            value: { exceedPrintLimit: false },
        });
    };

    const confirmPrintClose = () => {
        dispatch({
            type: ACTION_TYPES.SET_STATE_VALUES,
            value: { confirmPrintOpen: false },
        });
    };

    const confirmPrintClick = () => {
        dispatch({
            type: ACTION_TYPES.SET_STATE_VALUES,
            value: {
                printing: true,
            },
        });
        printCheck({
            variables: {
                ids: approvedRecords,
            },
        });
        confirmPrintClose();
    };

    const onCloseFormWithoutSave = () => {
        dispatch({ type: ACTION_TYPES.SET_STATE_VALUES, value: { couldLostData: false } });
    };

    return (
        <Dialog
            open
            onClose={onPopupClose}
            maxWidth="lg"
            fullScreen={isMobile}
            fullWidth={!isMobile}
            disableBackdropClick
            disableEscapeKeyDown
            scroll="paper"
            TransitionComponent={Transition}
        >
            <AppBar className={classes.appBar}>
                <Toolbar className={classes.centerItems}>
                    <div className={classes.buttonSpacing}>
                        <Button
                            variant="contained"
                            startIcon={<CachedIcon />}
                            onClick={() => reload()}
                            disabled={loading}
                            size="small"
                        >
                            Reload
                        </Button>
                        {actionType === GeneralAction.APPROVE
                        && (
                            <Badge badgeContent={approvedRecords?.length ?? 0} color="primary">
                                <Button
                                    className={classes.buttonSave}
                                    startIcon={<ThumbUpAltOutlinedIcon />}
                                    size="small"
                                    disabled={!isDirty}
                                    onClick={() => dispatch({
                                        type: ACTION_TYPES.SET_STATE_VALUES,
                                        value: { confirmApproveOpen: true },
                                    })}
                                >
                                    Approve
                                </Button>
                            </Badge>
                        )}
                        {actionType === GeneralAction.PRINT
                        && (
                            <Badge badgeContent={approvedRecords?.length ?? 0} color="primary">
                                <Button
                                    className={classes.buttonSave}
                                    startIcon={<PrintOutlinedIcon />}
                                    size="small"
                                    disabled={!isDirty || printing}
                                    onClick={() => {
                                        dispatch({
                                            type: ACTION_TYPES.SET_STATE_VALUES,
                                            value: {
                                                confirmPrintOpen: true,
                                            },
                                        });
                                    }}
                                >
                                    Print
                                </Button>
                            </Badge>
                        )}
                    </div>
                    <div className={classes.centerItems}>
                        {!isMobile && (
                            <Typography variant="h6" className={classes.title}>
                                {actionType === GeneralAction.APPROVE ? 'Approve Purchase Orders' : 'Print Purchase Orders'}
                            </Typography>
                        )}
                        <IconButton edge="start" color="inherit" onClick={onPopupClose}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                </Toolbar>
            </AppBar>
            <DialogContent className={classes.dialogContent}>
                <div className={classes.main}>
                    <Header>
                        <div className={classes.containerFilterSearch}>
                            <InputSearch
                                customClasses={clsx(classes.search, isMobile ? classes.searchFull : '')}
                                initialSearch={params.search || ''}
                                onSearch={onSearch}
                            />
                            {!isMobile && (
                                <LabelValue
                                    label="Items"
                                    value={table.totalCount}
                                />
                            )}
                            {isMobile && (
                                <Button
                                    variant="contained"
                                    endIcon={checkedAll ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                                    size="small"
                                    onClick={() => dispatch({ type: ACTION_TYPES.SET_APPROVE_ALL, value: !checkedAll })}
                                >
                                    {checkedAll ? 'Deselect All' : 'Select All'}
                                </Button>
                            )}
                        </div>
                    </Header>
                    {isMobile && loading && <div className={classes.centerDiv}><CircularProgress size="1em" color="inherit" /></div>}
                    {isMobile && !loading && showMobileLayout }
                    {!isMobile && (
                        <div className={classes.mainTable}>
                            <Table
                                data={table?.records}
                                columns={columns}
                                cursor="default"
                                load={loading}
                                totalRecords={table?.totalCount}
                                loadMore={loadMore}
                                rowSelected
                                className="-highlight"
                            />
                        </div>
                    )}
                </div>
            </DialogContent>
            <ConfirmDialog
                title="Attention - You have unsaved changes!"
                description="Do you want to close without saving?"
                open={couldLostData}
                variant="outlined"
                titlePrimary="Close"
                titleSecondary="Cancel"
                onClose={onCloseFormWithoutSave}
                onClickSecondary={onCloseFormWithoutSave}
                onClickPrimary={(e) => onPopupClose(e, true)}
            />

            <ConfirmDialog
                title="Exceeding print limit!"
                description={`You cannot print more than ${printLimit} records at the same time.`}
                open={exceedPrintLimit}
                variant="outlined"
                titlePrimary="Ok"
                titleSecondary="Cancel"
                onClose={exceedPrintClose}
                onClickSecondary={exceedPrintClose}
                onClickPrimary={exceedPrintClose}
            />
            <ConfirmDialog
                title="Approving batch purchase orders!"
                description="Are you sure you want to approve all these records?"
                open={confirmApproveOpen && !isSaving}
                variant="outlined"
                titlePrimary="Yes"
                titleSecondary="Cancel"
                onClose={() => dispatch({ type: ACTION_TYPES.SET_STATE_VALUES, value: { confirmApproveOpen: false } })}
                onClickSecondary={() => dispatch({ type: ACTION_TYPES.SET_STATE_VALUES, value: { confirmApproveOpen: false } })}
                onClickPrimary={() => onSave()}
            />
            <ConfirmDialog
                title="Confirming print!"
                description={printConfirmMessage}
                open={confirmPrintOpen && !printing}
                variant="outlined"
                titlePrimary="Yes"
                titleSecondary="Cancel"
                onClose={confirmPrintClose}
                onClickSecondary={confirmPrintClose}
                onClickPrimary={confirmPrintClick}
            />
            {isSaving && <DialogActionMessage message="Approving records... " />}
            {printing && <DialogActionMessage message="Printing... " />}
        </Dialog>
    );
};

PurchaseActionList.propTypes = {
    actionType: PropTypes.string,
};

PurchaseActionList.defaultProps = {
    actionType: GeneralAction.APPROVE,
};

export default PurchaseActionList;
